/**
 * String-literal union types for the `details.reason` field carried in
 * `ApiResponse.error.details`. Centralizing these prevents drift between
 * the backend's throws and the CLI's hint-lookup table.
 */

export type BadRequestReason =
  | "body_not_json"
  | "empty_body"
  | "empty_patch"
  | "empty_patch_would_orphan"
  | "missing_agent_name"
  | "invalid_agent_name"
  | "empty_agent_name"
  | "cross_agent_conversation"
  | "title_required"
  | "title_empty"
  | "title_too_long"
  | "missing_handle"
  | "invalid_handle"
  | "empty_handle"
  | "handle_too_long"
  | "missing_display_name"
  | "invalid_display_name"
  | "display_name_empty"
  | "display_name_too_long"
  | "missing_identities"
  | "no_identities_provided"
  | "invalid_identities"
  | "invalid_channel_id"
  | "channel_id_too_long"
  | "channel_id_empty"
  | "unknown_channel"
  | "invalid_add_identities"
  | "invalid_remove_identities"
  | "missing_channel"
  | "invalid_channel"
  | "missing_binding_id"
  | "invalid_binding_id"
  | "empty_binding_id"
  | "binding_id_too_long"
  | "missing_bot_token"
  | "bot_token_empty"
  | "bot_token_too_long"
  | "no_adapter_registered"
  | "invalid_status_filter"
  | "invalid_channel_filter"
  | "cleanup_active_binding"
  | "invalid_bot_username"
  | "bot_username_too_long"
  | "channel_mismatch"
  | "webhook_payload_invalid"
  | "webhook_registration_rejected"
  // Delegated end-user identity (see delegated-identity.ts). Both are refusals
  // of a MALFORMED or ILLEGITIMATE assertion, hence 400 rather than 401: the
  // caller authenticated fine, the assertion is what is wrong.
  | "invalid_end_user_key"
  | "end_user_key_not_permitted";

export type UnauthenticatedReason =
  | "missing_bearer"
  | "token_decode_failed"
  | "token_missing_identity"
  | "header_missing"
  | "downstream_auth_pattern"
  | "webhook_signature_invalid"
  // A service caller reached a conversation-scoped route without asserting a
  // delegated end-user identity. No stable identity → no conversation; the
  // remedy is the header, so the message names it.
  | "end_user_key_required";

export type NotFoundReason =
  | "team_member_not_found"
  | "agent_not_found"
  | "conversation_not_found"
  | "binding_not_found";

export type ConflictReason = "handle_exists" | "identity_claimed" | "binding_exists";

export type UpstreamReason =
  | "downstream_auth_pattern"
  | "table_storage_partial_write"
  | "table_storage_unavailable"
  | "key_vault_unavailable"
  | "kv_secret_malformed"
  | "cascade_partial_failure"
  | "service_bus_unavailable"
  | "webhook_registration_failed";

export type InternalReason = "config";
